import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PiAuthContext = createContext(null);

function isInsidePiBrowser() {
  if (typeof window === "undefined") return false;
  if (window.__piSdkBridge) return true;
  if (/PiBrowser/i.test(navigator.userAgent || "")) return true;
  if (window.Pi && typeof window.Pi.authenticate === "function") return true;
  return false;
}

export const PiAuthProvider = ({ children }) => {
  const { loginWithPiToken } = useAuth();
  const [piUser, setPiUser] = useState(null);
  const [piUsername, setPiUsername] = useState(null);
  const [sdkReady, setSdkReady] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const initCalled = useRef(false);

  // ── Handle an incomplete (interrupted) payment ──
  const onIncompletePaymentFound = useCallback(async (payment) => {
    const paymentId = payment?.identifier;
    if (!paymentId) return;
    console.warn("[Pi] Incomplete payment found:", paymentId);
    try {
      await axios.post(`${API}/pi/payments/incomplete`, { paymentId });
      console.log("[Pi] Incomplete payment resolved:", paymentId);
    } catch (err) {
      console.error("[Pi] Failed to resolve incomplete payment:", err);
    }
  }, []);

  // ── Core authenticate: call Pi.authenticate then verify on backend ──
  const doAuthenticate = useCallback(async () => {
    // Scopes include "payments" so Pi.createPayment works
    const authResult = await window.Pi.authenticate(
      ["username", "payments"],
      onIncompletePaymentFound
    );

    const { accessToken, user } = authResult;
    setPiUsername(user?.username);

    const res = await axios.post(`${API}/auth/pi`, { accessToken });
    const verified = res.data;

    localStorage.setItem("userToken", verified.token);
    setPiUser(verified.user);
    loginWithPiToken(verified.user, verified.token);

    return verified;
  }, [loginWithPiToken, onIncompletePaymentFound]);

  // ── Init + auto-auth on mount ──
  useEffect(() => {
    if (initCalled.current) return;
    initCalled.current = true;

    const boot = async () => {
      if (!window.Pi) {
        setLoading(false);
        return;
      }

      try {
        await window.Pi.init({ version: "2.0", sandbox: false });
        setSdkReady(true);
        await doAuthenticate();
      } catch (err) {
        console.warn("[Pi] Auto-auth did not complete:", err?.message || err);
        setError(null);
      }
      setLoading(false);
    };
    boot();
  }, [doAuthenticate]);

  // ── Manual sign-in ──
  const signInWithPi = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (!window.Pi) throw new Error("Pi Network is only available inside the Pi Browser.");
      if (!sdkReady) {
        await window.Pi.init({ version: "2.0", sandbox: false });
        setSdkReady(true);
      }
      const result = await doAuthenticate();
      setLoading(false);
      return result;
    } catch (err) {
      setError(err?.message || "Pi sign-in failed");
      setLoading(false);
      throw err;
    }
  }, [sdkReady, doAuthenticate]);

  // ── Create a Pi payment (U2A) ──
  const createPiPayment = useCallback(async ({ amount, memo, metadata }) => {
    if (!window.Pi) throw new Error("Pi SDK not available");
    if (!sdkReady) throw new Error("Pi SDK not initialised");

    return new Promise((resolve, reject) => {
      window.Pi.createPayment(
        {
          amount,
          memo,
          metadata,
        },
        {
          onReadyForServerApproval: async (paymentId) => {
            try {
              await axios.post(`${API}/pi/payments/approve`, { paymentId });
            } catch (err) {
              console.error("[Pi Pay] Server approval failed:", err);
              reject(err);
            }
          },
          onReadyForServerCompletion: async (paymentId, txid) => {
            try {
              const res = await axios.post(`${API}/pi/payments/complete`, { paymentId, txid });
              resolve(res.data);
            } catch (err) {
              console.error("[Pi Pay] Server completion failed:", err);
              reject(err);
            }
          },
          onCancel: (paymentId) => {
            console.log("[Pi Pay] User cancelled:", paymentId);
            reject(new Error("Payment cancelled"));
          },
          onError: (error, payment) => {
            console.error("[Pi Pay] Error:", error, payment);
            reject(new Error(error?.message || "Pi payment error"));
          },
        }
      );
    });
  }, [sdkReady]);

  const signOut = useCallback(() => {
    setPiUser(null);
    setPiUsername(null);
    localStorage.removeItem("userToken");
  }, []);

  return (
    <PiAuthContext.Provider value={{
      piUser, piUsername, sdkReady, loading, error,
      signInWithPi, signOut, createPiPayment,
      isAuthenticated: !!piUser,
    }}>
      {children}
    </PiAuthContext.Provider>
  );
};

export const usePiAuth = () => useContext(PiAuthContext);
export default PiAuthContext;
